import React from 'react'
import htmlParser from 'html-react-parser'
import Head from 'next/head'
import { isEmpty } from 'src/utils/string'
import { isObjectEmpty } from 'src/utils/object'
import {
  ADWORDS_CONVERSION_ID,
  FACEBOOK_PIXEL_ID,
  GA_TRACKING_ID,
  GTM_CONTAINER_ID,
  ODDLE_PASS_JS_URL,
  SEGMENT_API_KEY,
  SG_ODDLE_EATS_AW_TRACKING_ID,
  SG_ODDLE_EATS_GA_TRACKING_ID,
  SG_ODDLE_EATS_PIXEL_ID
} from 'config/client'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  selectDetailMenuSite,
  selectScripts,
  selectTrackers
} from 'src/redux/meta'
import { useRouter } from 'next/router'

const webpageTypes: {
  others: number
  checkout: number
  thankyou: number
} = {
  others: 0,
  checkout: 1,
  thankyou: 2
}

const appendTypes: {
  head: number
  body: number
} = {
  head: 0,
  body: 1
}

const matchCurrentPageType = (webpageType, pathName) => {
  // Temporary disable all external scripts for loyalty page before have solid product direction.

  if (pathName === '/[locale]/loyalty') return false

  // only for checkout & thankyou
  const current = pathName.replace('/[locale]/', '')

  return (
    webpageType === webpageTypes[current] ||
    (!webpageTypes[current] && webpageType === webpageTypes.others)
  )
}

interface ExternalScriptsProps {
  Container?: typeof React.Fragment | typeof React.Component
}

const ExternalScripts = ({
  Container = React.Fragment
}: ExternalScriptsProps): JSX.Element => {
  const meta = useAppSelector(selectDetailMenuSite)
  const trackers = useAppSelector(selectTrackers)
  const scripts = useAppSelector(selectScripts)
  const router = useRouter()
  const fromOddleEatsSG = () => {
    /**
     * If referrer is found in the url parameters
     * and the value is evaluated to oddle-eats
     *
     * we will assume that the traffic is coming from Oddle Eats.
     * thus we initiate fb pixel with Oddle Eats pixel id to track the user's journey
     */
    const { referrer, utm_source: utmSource } = router.query
    return referrer === 'oddle-eats' && utmSource === 'oddle-eats'
  }

  const renderGTag = () => {
    const ga =
      (trackers || []).find((t) => t.trackingClient === 'GoogleAnalytics') ||
      ({} as {
        trackingId: string
      })

    const gtags = [
      ...GA_TRACKING_ID.split(';'),
      ADWORDS_CONVERSION_ID,
      ga.trackingId
    ].filter((gtag) => !isEmpty(gtag))

    if (fromOddleEatsSG()) {
      gtags.push(SG_ODDLE_EATS_GA_TRACKING_ID)
      gtags.push(SG_ODDLE_EATS_AW_TRACKING_ID)
    }

    return (
      <React.Fragment>
        {GA_TRACKING_ID.split(';').map((tag) => (
          <script
            key={tag}
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${tag}`}
          />
        ))}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              ${(gtags || [])
                .map((gtag) => `gtag('config', '${gtag}');`)
                .join('\n\t\t\t')}
            `
          }}
        />
      </React.Fragment>
    )
  }

  const renderPixel = () => {
    const pixel = (trackers || []).find(
      (t) => t.trackingClient === 'FacebookPixel'
    )

    /**
     * collect pixelIDs set by merchant in the OMS dashboard.
     * Merchant is allowed to add up to 5 pixel Ids separated by comma, i.e.
     *
     * 12345,23456,34567,45678,56789
     */
    const pixelIds = !isObjectEmpty(pixel)
      ? (pixel.trackingId || '')
          .split(',')
          .map((id) => !isEmpty(id) && id.replace(/[^a-zA-Z0-9]/g, ''))
          .filter((id) => !isEmpty(id))
          .slice(0, 5)
      : []

    if (fromOddleEatsSG() && SG_ODDLE_EATS_PIXEL_ID) {
      pixelIds.push(SG_ODDLE_EATS_PIXEL_ID)
    }

    // Remove FACEBOOK_PIXEL_ID from pixelIds if it exists to avoid duplicate initialization
    const uniquePixelIds = pixelIds.filter(id => id !== FACEBOOK_PIXEL_ID)

    return (
      <script
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('set', 'autoConfig', false, '${FACEBOOK_PIXEL_ID}');
            fbq('init', '${FACEBOOK_PIXEL_ID}');
            ${
              (uniquePixelIds || []).length > 0
                ? (uniquePixelIds || [])
                    .filter(id => id) // Additional filter to ensure no null/undefined IDs
                    .map((pixelId) => {
                      return (
                        `fbq('set', 'autoConfig', false, '${pixelId}');` +
                        `\n\t\t\t` +
                        `fbq('init', '${pixelId}');`
                      )
                    })
                    .join('\n\t\t\t')
                : ''
            }
          `
        }}
      />
    )
  }

  const renderGTM = () => {
    return (
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
          `
        }}
      />
    )
  }

  const renderScripts = (appendType) => {
    const components = htmlParser(
      scripts
        .filter(
          (script) =>
            matchCurrentPageType(script.webpageType, router.pathname) &&
            script.appendType === appendType &&
            script.trackerScript
        )
        .map((script) => script.trackerScript)
        .join('')
    )
    const validComponents = Array.isArray(components)
      ? components.filter(React.isValidElement)
      : React.isValidElement(components)
      ? components
      : null
    return <React.Fragment>{validComponents}</React.Fragment>
  }

  const renderSegment = () => {
    const customerSegmentEnabled = meta?.customerSegmentEnabled
    if (customerSegmentEnabled) {
      return (
        <script
          dangerouslySetInnerHTML={{
            __html: `
            !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.13.1";
            analytics.load("${SEGMENT_API_KEY}");
            }}();
          `
          }}
        />
      )
    }
    return <React.Fragment />
  }

  return (
    <Container>
      <Head>
        {renderGTag()}
        {renderPixel()}
        {renderGTM()}
        {renderScripts(appendTypes.head)}
        {renderSegment()}
        <script src={ODDLE_PASS_JS_URL} />
      </Head>
      {renderScripts(appendTypes.body)}
    </Container>
  )
}

export default ExternalScripts
